/**
 * Record 편집/재처리 API wrapper.
 *
 * 백엔드 엔드포인트:
 *   PATCH  /api/jobs/{job_id}/files/{file_id}/record
 *   POST   /api/jobs/{job_id}/files/{file_id}/reprocess
 *   GET    /api/jobs/{job_id}/files/{file_id}/record/diff
 *   POST   /api/jobs/{job_id}/files/{file_id}/record/apply
 *   DELETE /api/jobs/{job_id}/files/{file_id}/record/pending
 */

import { currentApiLocale } from "@/lib/locale";

import { apiClient } from "./client";
import type { RecordOut } from "./jobs";

// ────────────── 1. 편집 ──────────────

export interface EditRecordPayload {
  csv_data?: Record<string, string>;
  is_rejected?: boolean;
  rejection_reason?: string | null;
  memo?: string;
}

export async function editRecord(
  jobId: string,
  fileId: string,
  payload: EditRecordPayload,
): Promise<RecordOut> {
  const res = await apiClient.patch<RecordOut>(`/jobs/${jobId}/files/${fileId}/record`, payload);
  return res.data;
}

// ────────────── 1-b. 검수 완료 + 최종 AI 검토 (M21 #7) ──────────────

/** 1차 검수 완료 처리 + 최종 AI 검토(chat-vl-pro) 트리거. */
export async function setReviewComplete(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.post<RecordOut>(
    `/jobs/${jobId}/files/${fileId}/review-complete`,
    undefined,
    { params: { locale: currentApiLocale() } },
  );
  return res.data;
}

/** 검수 완료 해제 (상태/AI 검토 결과 초기화). */
export async function clearReviewComplete(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.delete<RecordOut>(`/jobs/${jobId}/files/${fileId}/review-complete`);
  return res.data;
}

/** 2차 검수 완료 (AI 지적 확인 후 최종 사인오프). */
export async function setReviewConfirmed(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.post<RecordOut>(`/jobs/${jobId}/files/${fileId}/review-confirm`);
  return res.data;
}

/** 2차 검수 완료 해제 (이전 AI 판정 상태로 복귀). */
export async function clearReviewConfirmed(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.delete<RecordOut>(`/jobs/${jobId}/files/${fileId}/review-confirm`);
  return res.data;
}

/** 자동 검수가 고친 값 확인함 — 목록의 "확인 권장" 표시를 끈다. */
export async function setAutoFixChecked(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.post<RecordOut>(`/jobs/${jobId}/files/${fileId}/auto-fix-check`);
  return res.data;
}

/** 고친 값 확인 취소 — "확인 권장" 표시를 다시 켠다. */
export async function clearAutoFixChecked(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.delete<RecordOut>(`/jobs/${jobId}/files/${fileId}/auto-fix-check`);
  return res.data;
}

// ────────────── 2. 재처리 ──────────────

export interface ReprocessOut {
  task_id: string | null;
  file_id: string;
}

export async function requestReprocess(jobId: string, fileId: string): Promise<ReprocessOut> {
  const res = await apiClient.post<ReprocessOut>(`/jobs/${jobId}/files/${fileId}/reprocess`);
  return res.data;
}

export interface DiffFieldRow {
  field: string;
  current: unknown;
  pending: unknown;
  changed: boolean;
}

export interface DiffRejectionBlock {
  current: { is_rejected: boolean; rejection_reason: string | null };
  pending: { is_rejected: boolean; rejection_reason: string | null };
  changed: boolean;
}

export interface CorrectionDiffInfo {
  request_id: string;
  feedback: string;
  reasoning: string;
  annotation_count: number;
}

export interface RecordDiffOut {
  file_id: string;
  record_id: string;
  extracted_at: string | null;
  triggered_by_user_id: number | null;
  success: boolean;
  error_message: string | null;
  csv_fields: DiffFieldRow[];
  rejection: DiffRejectionBlock;
  amount_analysis: {
    current: Record<string, unknown> | null;
    pending: Record<string, unknown> | null;
  };
  /** M15 — 결과 출처. "reprocess"(기본) | "correction". */
  source: "reprocess" | "correction";
  correction: CorrectionDiffInfo | null;
  /** M15 — pending 결과의 필드 bbox (검토 PDF 오버레이용). */
  csv_bbox: Record<string, unknown> | null;
}

export async function getRecordDiff(jobId: string, fileId: string): Promise<RecordDiffOut> {
  const res = await apiClient.get<RecordDiffOut>(`/jobs/${jobId}/files/${fileId}/record/diff`);
  return res.data;
}

export type FieldChoice = "use_pending" | "use_current";
export type RejectionChoice = "use_pending" | "use_current" | "skip";

export interface ApplyReextractionPayload {
  field_choices: Record<string, FieldChoice>;
  apply_rejection: RejectionChoice;
  apply_amount_analysis: boolean;
  memo?: string;
}

export async function applyReextraction(
  jobId: string,
  fileId: string,
  payload: ApplyReextractionPayload,
): Promise<RecordOut> {
  const res = await apiClient.post<RecordOut>(
    `/jobs/${jobId}/files/${fileId}/record/apply`,
    payload,
  );
  return res.data;
}

export async function discardPending(jobId: string, fileId: string): Promise<RecordOut> {
  const res = await apiClient.delete<RecordOut>(`/jobs/${jobId}/files/${fileId}/record/pending`);
  return res.data;
}
